import type { I18nT } from "@lib/dashboardI18n";

import { aspectRatioFilterValueToDecimal } from "../constants";
import type { AspectRatio, AspectRatioWithTolerance } from "../types";

/**
 * Given an aspect ratio name, get a translated label and the decimal value we will send to the server for filtering on that aspect ratio in the media library
 */
export const getAspectRatioInfo = (
  t: I18nT<"media-library">,
  aspectRatio: AspectRatio,
  tolerance = 0.1
): AspectRatioWithTolerance => {
  const aspectRatioInfo: { [Ratio in AspectRatio]: { label: string; aspectRatioDecimal: number } } = {
    "1:1": {
      label: t("aspect-ratio-1-1", "1:1"),
      aspectRatioDecimal: aspectRatioFilterValueToDecimal["1:1"],
    },
    "2:1": {
      label: t("aspect-ratio-2-1", "2:1"),
      aspectRatioDecimal: aspectRatioFilterValueToDecimal["2:1"],
    },
    "3:2": {
      label: t("aspect-ratio-3-2", "3:2"),
      aspectRatioDecimal: aspectRatioFilterValueToDecimal["3:2"],
    },
    "3:4": {
      label: t("aspect-ratio-3-4", "3:4"),
      aspectRatioDecimal: aspectRatioFilterValueToDecimal["3:4"],
    },
    "3:5": {
      label: t("aspect-ratio-3-5", "3:5"),
      aspectRatioDecimal: aspectRatioFilterValueToDecimal["3:5"],
    },
    "4:3": {
      label: t("aspect-ratio-4-3", "4:3"),
      aspectRatioDecimal: aspectRatioFilterValueToDecimal["4:3"],
    },
    "5:3": {
      label: t("aspect-ratio-5-3", "5:3"),
      aspectRatioDecimal: aspectRatioFilterValueToDecimal["5:3"],
    },
    "5:4": {
      label: t("aspect-ratio-5-4", "5:4"),
      aspectRatioDecimal: aspectRatioFilterValueToDecimal["5:4"],
    },
    "6:1": {
      label: t("aspect-ratio-6-1", "6:1"),
      aspectRatioDecimal: aspectRatioFilterValueToDecimal["6:1"],
    },
    "6:5": {
      label: t("aspect-ratio-6-5", "6:5"),
      aspectRatioDecimal: aspectRatioFilterValueToDecimal["6:5"],
    },
    "10:3": {
      label: t("aspect-ratio-10-3", "10:3"),
      aspectRatioDecimal: aspectRatioFilterValueToDecimal["10:3"],
    },
    "10:16": {
      label: t("aspect-ratio-10-16", "10:16"),
      aspectRatioDecimal: aspectRatioFilterValueToDecimal["10:16"],
    },
    "16:5": {
      label: t("aspect-ratio-16-5", "16:5"),
      aspectRatioDecimal: aspectRatioFilterValueToDecimal["16:5"],
    },
    "16:10": {
      label: t("aspect-ratio-16-10", "16:10"),
      aspectRatioDecimal: aspectRatioFilterValueToDecimal["16:10"],
    },
    "29:10": {
      label: t("aspect-ratio-29-10", "29:10"),
      aspectRatioDecimal: aspectRatioFilterValueToDecimal["29:10"],
    },
  };
  if (!aspectRatioInfo[aspectRatio]) {
    return {
      aspectRatioType: aspectRatio,
      label: aspectRatio,
      aspectRatioDecimal: aspectRatio
        .split(":")
        .map(Number)
        .reduce((divisor, dividend) => divisor / dividend),
      tolerance,
    };
  }
  return {
    ...aspectRatioInfo[aspectRatio],
    aspectRatioType: aspectRatio,
    tolerance,
  };
};
